/**
 * AnsellGlovesPage — 547 Ansell glove product families, retail-only.
 * Route: /marketplace/gloves/ansell
 *
 * Data source: GET /api/gloves/ansell (public, retail-only). Cost prices
 * are NEVER shipped to customers — admins see cost via /admin/gloves-pricing.
 *
 * Ansell brand ecosystem covered:
 *   HyFlex · ActivArmr · MICROFLEX · KleenGuard · BioClean · EDGE ·
 *   TouchNTuff · GAMMEX · Micro-Touch · AlphaTec · Encore · VibraGuard ·
 *   Solvex · KT (Kimtech) · Neptune · Acitek
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, ShieldCheck, Package, Loader2, Search, ShoppingCart } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";
import GloveBrandTabs from "@/components/GloveBrandTabs";
import { API } from "@/App";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import { UOM_LABELS } from "@/data/glovesCatalog";
import { toast } from "sonner";

const fmt = (n) => (typeof n === "number" ? `$${n.toFixed(2)}` : "—");

// Ansell brand palette
const BRAND_ACCENT = {
  HYFLEX: "bg-blue-100 text-blue-800 border-blue-200",
  ACTIVARMR: "bg-orange-100 text-orange-800 border-orange-200",
  MICROFLEX: "bg-purple-100 text-purple-800 border-purple-200",
  TOUCHNTUFF: "bg-emerald-100 text-emerald-800 border-emerald-200",
  GAMMEX: "bg-cyan-100 text-cyan-800 border-cyan-200",
  "MICRO-TOUCH": "bg-pink-100 text-pink-800 border-pink-200",
  ALPHATEC: "bg-amber-100 text-amber-800 border-amber-200",
  EDGE: "bg-slate-100 text-slate-800 border-slate-200",
  KleenGuard: "bg-lime-100 text-lime-800 border-lime-200",
  BIOCLN: "bg-teal-100 text-teal-800 border-teal-200",
  ENCORE: "bg-indigo-100 text-indigo-800 border-indigo-200",
  DEFAULT: "bg-slate-100 text-slate-700 border-slate-200",
};

const MATERIALS = [
  { key: "all", label: "All Materials" },
  { key: "nitrile", label: "Nitrile" },
  { key: "latex", label: "Latex / PI" },
  { key: "neoprene", label: "Neoprene" },
  { key: "vinyl", label: "Vinyl" },
  { key: "poly", label: "Poly" },
];

export default function AnsellGlovesPage() {
  const [products, setProducts] = useState([]);
  const [enrichedCount, setEnrichedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [material, setMaterial] = useState("all");
  const [selectedSize, setSelectedSize] = useState({});
  const [specsSlug, setSpecsSlug] = useState(null);
  const { addItem } = useQuoteCart();

  useEffect(() => {
    axios
      .get(`${API}/gloves/ansell`)
      .then((res) => {
        setProducts(res.data.products || []);
        setEnrichedCount(res.data.enriched || 0);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to load Ansell catalog");
        setLoading(false);
      });
  }, []);

  const brands = useMemo(() => {
    const counts = { all: products.length };
    for (const p of products) counts[p.brand] = (counts[p.brand] || 0) + 1;
    return counts;
  }, [products]);

  const brandKeys = useMemo(
    () =>
      ["all", ...Object.keys(brands).filter((b) => b !== "all").sort((a, b) => brands[b] - brands[a])],
    [brands]
  );

  const filtered = useMemo(() => {
    let list = products;
    if (brand !== "all") list = list.filter((p) => p.brand === brand);
    if (material !== "all") list = list.filter((p) => p.material === material);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          p.prodfam.toLowerCase().includes(needle) ||
          p.variants.some((v) => v.mrpn?.toLowerCase().includes(needle) || v.sku.toLowerCase().includes(needle))
      );
    }
    return list;
  }, [products, brand, material, q]);

  const variantFor = (p) => {
    const size = selectedSize[p.id] || p.variants[0]?.size;
    return p.variants.find((v) => v.size === size) || p.variants[0];
  };

  const handleAdd = (p) => {
    const v = variantFor(p);
    addItem(
      {
        erp: v?.mrpn || v?.sku,
        name: `${p.name} — Size ${v?.size || "N/A"}`,
        image: null,
        source: `Sterile Mate — Ansell · ${p.brand}`,
        pack: `UOM ${p.uom}`,
        price: v?.retail ?? p.retail,
      },
      1
    );
    toast.success(`Added: ${p.name} · Size ${v?.size}`);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ansell-gloves-page">
      <SiteHeader />
      <MarketplaceCategoryNav currentCategory="gloves" />
      <MarketplaceBreadcrumb
        trail={[
          { label: "Marketplace", to: "/marketplace" },
          { label: "Disposable Gloves", to: "/marketplace/gloves" },
          { label: "Ansell" },
        ]}
      />

      {/* HERO */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
          <p className="text-[11px] font-black uppercase tracking-[3px] text-blue-700 mb-3">
            Sterile Mate · Authorized Ansell Distributor
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-[1.05] tracking-tight mb-4"
            data-testid="ansell-hero-title"
          >
            The full Ansell glove line —<br />
            <span className="text-blue-600">on one PO.</span>
          </h1>
          <p className="text-lg text-slate-700 leading-relaxed mb-6 max-w-2xl">
            {products.length ? `${products.length} product families` : "…"} across HyFlex, ActivArmr, MICROFLEX, TouchNTuff, GAMMEX, EDGE, KleenGuard, BioClean and more.
            Industrial safety, exam, surgical, cleanroom, and cut-resistant lines.
          </p>
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Chemical · Cut · Impact · Exam · Surgical · Cleanroom
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
              <Package className="w-3.5 h-3.5 text-blue-600" /> UOM per-line · PC · PR · CA
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="mailto:orders@sterilemate.com?subject=Ansell%20Gloves%20-%20Quote%20Request"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-black text-sm transition-all shadow-sm"
              data-testid="ansell-hero-quote-cta"
            >
              <Mail className="w-4 h-4" /> Request full quote
            </a>
            <a
              href="tel:8009883892"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 hover:border-blue-600 bg-white text-slate-900 text-sm font-bold transition-all"
              data-testid="ansell-hero-phone"
            >
              <Phone className="w-4 h-4" /> 800.988.3892
            </a>
          </div>
        </div>
      </section>

      {/* SPEC SEARCH — full-text across mirrored specifications */}
      <AnsellSpecSearch onOpenSpecs={(slug) => setSpecsSlug(slug)} />

      {/* FILTER BAR */}
      <section className="border-b border-slate-200 bg-white sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3">
          <div className="mb-3 pb-3 border-b border-slate-100 overflow-x-auto no-scrollbar">
            <GloveBrandTabs />
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                placeholder="Search Ansell name, SKU, MRPN..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="ansell-search"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <select
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              data-testid="ansell-material-select"
              className="px-3 py-2 border border-slate-300 rounded-md text-sm font-bold text-slate-700 focus:outline-none focus:border-blue-500"
            >
              {MATERIALS.map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <span className="text-[10px] font-black uppercase tracking-[1.5px] text-slate-500 shrink-0 mr-1">Ansell line:</span>
            {brandKeys.map((b) => {
              const active = brand === b;
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBrand(b)}
                  data-testid={`ansell-brand-${b}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap transition-colors border ${
                    active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                  }`}
                >
                  {b === "all" ? "All Brands" : b}
                  <span className={`inline-flex items-center justify-center min-w-[22px] h-4 px-1 rounded text-[10px] font-black ${active ? "bg-white text-slate-900" : "bg-slate-100 text-slate-600"}`}>
                    {brands[b] || 0}
                  </span>
                </button>
              );
            })}
          </div>
          {/* UOM legend */}
          <div className="pt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500">
            <span className="font-black uppercase tracking-[1.5px] text-slate-400">UOM Legend:</span>
            {Object.values(UOM_LABELS).map((u) => (
              <span key={u.code} className="inline-flex items-center gap-1">
                <span className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[9px] font-black">{u.code}</span>
                <span className="text-slate-600 font-semibold">{u.label}</span>
                <span className="text-slate-400">— {u.desc}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {loading && (
          <div className="p-14 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading Ansell catalog…
          </div>
        )}
        {error && !loading && (
          <div className="p-8 rounded-lg border border-rose-200 bg-rose-50 text-rose-900">
            {error}
          </div>
        )}
        {!loading && !error && (
          <>
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
              <h2 className="text-2xl font-black text-slate-900">
                Ansell Gloves <span className="text-slate-500 font-medium">· {filtered.length} product lines showing</span>
              </h2>
              <Link to="/marketplace/gloves" className="text-xs font-bold text-blue-700 hover:underline" data-testid="ansell-back-to-ammex">
                ← Back to AMMEX / all gloves
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((p) => {
                const v = variantFor(p);
                const badge = BRAND_ACCENT[p.brand] || BRAND_ACCENT.DEFAULT;
                return (
                  <div
                    key={p.id}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:ring-2 hover:ring-blue-500/40 hover:shadow-md transition-all flex flex-col"
                    data-testid={`ansell-product-${p.id}`}
                  >
                    {/* Product image (enriched from ansell.com when available) */}
                    <div className="aspect-square bg-slate-50 relative overflow-hidden">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-contain p-3"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                          <Package className="w-10 h-10 mb-1" />
                          <span className="text-[10px] font-bold text-slate-400">No image yet</span>
                        </div>
                      )}
                      <span className={`absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border ${badge}`}>
                        {p.brand}
                      </span>
                      <span className="absolute top-2 right-2 text-[9px] font-black uppercase tracking-wider text-slate-500 bg-white/90 px-1.5 py-0.5 rounded">
                        {p.prodfam}
                      </span>
                      {p.pdf_thumbnail_url && (
                        <button
                          type="button"
                          onClick={() => setSpecsSlug(p.specs_slug)}
                          title={`${p.mirrored_pdf_count} datasheet${p.mirrored_pdf_count > 1 ? "s" : ""} hosted locally — click to preview`}
                          data-testid={`ansell-pdf-thumb-${p.id}`}
                          className="absolute bottom-2 right-2 w-14 h-16 rounded-md overflow-hidden border-2 border-white shadow-md ring-1 ring-slate-300 bg-white hover:ring-2 hover:ring-emerald-500 transition-all group"
                        >
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${p.pdf_thumbnail_url}`}
                            alt="Datasheet preview"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <span className="absolute inset-x-0 bottom-0 text-[8px] font-black uppercase tracking-widest text-white bg-emerald-600/90 py-0.5 text-center">
                            PDS
                          </span>
                        </button>
                      )}
                    </div>

                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="text-sm font-bold text-slate-900 leading-snug mb-1 min-h-[40px]">
                        {p.name}
                      </h3>
                      {p.tagline && (
                        <p className="text-[11px] text-slate-500 leading-snug mb-2 line-clamp-2 italic">
                          {p.tagline}
                        </p>
                      )}
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[1.5px] text-blue-700">
                          Price · per {UOM_LABELS[p.uom]?.label.toLowerCase() || "unit"}
                          <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded bg-blue-100 text-blue-700 text-[9px] font-black">
                            UOM: {p.uom}
                          </span>
                        </p>
                        <p className="text-lg font-black text-slate-900" data-testid={`ansell-retail-${p.id}`}>
                          {fmt(v?.retail ?? p.retail)}
                        </p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <p className="text-[10px] font-black uppercase tracking-[1.5px] text-slate-500 mb-1.5">Size ({p.variants.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {p.variants.slice(0, 12).map((vv) => {
                          const active = (selectedSize[p.id] || p.variants[0]?.size) === vv.size;
                          return (
                            <button
                              key={vv.sku}
                              type="button"
                              title={`SKU ${vv.sku} · MRPN ${vv.mrpn}`}
                              onClick={() => setSelectedSize((s) => ({ ...s, [p.id]: vv.size }))}
                              data-testid={`ansell-size-${p.id}-${vv.size}`}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black border transition-colors ${
                                active
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-slate-700 border-slate-300 hover:border-blue-500"
                              }`}
                            >
                              {vv.size}
                            </button>
                          );
                        })}
                        {p.variants.length > 12 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                            +{p.variants.length - 12} more
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1 font-mono truncate">
                        MRPN: <span className="text-slate-700 font-bold">{v?.mrpn || "—"}</span>
                      </p>
                    </div>
                    <div className="mt-auto flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleAdd(p)}
                        data-testid={`ansell-add-${p.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-black text-xs transition-all"
                      >
                        <ShoppingCart className="w-3.5 h-3.5" /> Add to Quote
                      </button>
                      {p.has_specs && (
                        <button
                          type="button"
                          onClick={() => setSpecsSlug(p.specs_slug)}
                          title="View full specifications"
                          data-testid={`ansell-specs-${p.id}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/></svg>
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filtered.length === 0 && (
              <div className="p-14 text-center text-slate-500">
                No Ansell products match your filters.
              </div>
            )}
          </>
        )}
      </section>
      {specsSlug && (
        <AnsellSpecsModal slug={specsSlug} onClose={() => setSpecsSlug(null)} />
      )}
    </div>
  );
}

function AnsellSpecSearch({ onOpenSpecs }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      fetch(`${API}/gloves/ansell/search?q=${encodeURIComponent(term)}`)
        .then((r) => r.json())
        .then((d) => { setResults(d.results || []); setLoading(false); })
        .catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <section className="border-b border-slate-200 bg-gradient-to-r from-blue-50 to-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-700 mb-1">
              Spec Search — jump to a datasheet
            </p>
            <p className="text-sm text-slate-600">
              Try “chemo tested nitrile”, “ANSI A5 cut”, “automotive aftermarket”, or “fully textured”.
            </p>
          </div>
          <div className="mt-3 sm:mt-0 sm:ml-auto flex-1 sm:max-w-xl relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 200)}
              placeholder="Search specs, standards, applications…"
              data-testid="ansell-spec-search"
              className="w-full pl-10 pr-4 py-3 border-2 border-slate-300 rounded-xl text-sm bg-white shadow-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20"
            />
            {open && q.trim().length >= 2 && (
              <div className="absolute z-40 top-full mt-2 w-full bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden max-h-[420px] overflow-y-auto">
                {loading && (
                  <div className="p-4 text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                  </div>
                )}
                {!loading && results.length === 0 && (
                  <div className="p-4 text-xs text-slate-500">
                    No spec match. Try broader keywords like “nitrile” or “cut resistant”.
                  </div>
                )}
                {!loading && results.map((r) => (
                  <button
                    key={r.slug}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onOpenSpecs(r.slug); setOpen(false); }}
                    data-testid={`ansell-spec-search-result-${r.slug}`}
                    className="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-blue-50 flex items-start gap-3 group"
                  >
                    {r.image ? (
                      <img src={r.image} alt="" className="w-11 h-11 rounded object-contain bg-slate-50 shrink-0" />
                    ) : (
                      <div className="w-11 h-11 rounded bg-slate-100 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-black text-slate-900 truncate group-hover:text-blue-700">{r.name}</p>
                        {r.has_mirrored_pdf && (
                          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                            PDF ×{r.mirrored_pdf_count}
                          </span>
                        )}
                      </div>
                      {r.snippet && (
                        <p className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{r.snippet}</p>
                      )}
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {r.matched_terms.map((t) => (
                          <span key={t} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900">{t}</span>
                        ))}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function AnsellSpecsModal({ slug, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("description");
  const [error, setError] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancel = false;
    setData(null); setError(null);
    fetch(`${API}/api/gloves/ansell/specs/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!cancel) setData(d); })
      .catch((s) => { if (!cancel) setError(s); });
    return () => { cancel = true; };
  }, [API, slug]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const tabs = [
    { id: "description", label: "Description" },
    { id: "specs", label: "Specifications" },
    { id: "standards", label: "Standards" },
    { id: "downloads", label: "Downloads" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="ansell-specs-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-600">
              Ansell Specifications
            </p>
            <h3 className="text-xl font-black text-slate-900 mt-0.5">
              {data?.slug ? data.slug.toUpperCase().replace(/-/g, " ") : slug}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="ansell-specs-close"
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-2xl text-slate-500"
          >×</button>
        </header>

        {!data && !error && (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            Loading specifications…
          </div>
        )}
        {error && (
          <div className="flex-1 flex items-center justify-center text-center px-8">
            <div>
              <p className="font-black text-slate-800">Specs coming soon</p>
              <p className="text-sm text-slate-500 mt-1">
                We&apos;re mirroring the full spec sheet for this product. In the
                meantime, please call <span className="font-bold">800.988.3892</span> for
                material, gauge, sizing and datasheet PDFs.
              </p>
            </div>
          </div>
        )}
        {data && (
          <>
            <nav className="px-6 flex gap-1 border-b bg-slate-50">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  data-testid={`ansell-specs-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-2.5 text-xs font-bold border-b-2 transition-colors ${
                    tab === t.id
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
            <div className="flex-1 overflow-y-auto p-6">
              {tab === "description" && (
                <div className="space-y-4">
                  {data.keyFeatures?.length > 0 && (
                    <div>
                      <p className="text-xs font-black uppercase text-slate-500 mb-2">Key Features</p>
                      <ul className="space-y-1.5">
                        {data.keyFeatures.map((f, i) => (
                          <li key={i} className="text-sm text-slate-800 flex gap-2">
                            <span className="text-blue-600">•</span><span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.description?.length > 0 && (
                    <div>
                      <p className="text-xs font-black uppercase text-slate-500 mb-2">Description</p>
                      <ul className="space-y-2">
                        {data.description.map((d, i) => (
                          <li key={i} className="text-sm text-slate-700 leading-relaxed">{d}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.recommendedFor?.length > 0 && (
                    <div>
                      <p className="text-xs font-black uppercase text-slate-500 mb-2">Recommended For</p>
                      <div className="flex flex-wrap gap-1.5">
                        {data.recommendedFor.map((r, i) => (
                          <span key={i} className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">{r}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {tab === "specs" && (
                <div className="space-y-4">
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(data.specs || {}).map(([k, v]) => (
                        <tr key={k} className="border-b last:border-0">
                          <td className="py-2 pr-4 text-xs font-black text-slate-500 uppercase w-1/3 align-top">{k}</td>
                          <td className="py-2 text-slate-800">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.packaging && (
                    <div className="mt-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
                      <p className="text-xs font-black uppercase text-blue-700 mb-1">Packaging</p>
                      <p className="text-sm text-slate-800">{data.packaging}</p>
                    </div>
                  )}
                  {data.industries?.length > 0 && (
                    <div>
                      <p className="text-xs font-black uppercase text-slate-500 mb-2">Primary Industries</p>
                      <div className="flex flex-wrap gap-1.5">
                        {data.industries.map((i, k) => (
                          <span key={k} className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">{i}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {tab === "standards" && (
                <div>
                  {data.standards?.length > 0 ? (
                    <ul className="space-y-2">
                      {data.standards.map((s, i) => (
                        <li key={i} className="p-3 rounded-lg border border-slate-200 text-sm font-mono text-slate-800">{s}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500">No certifications listed.</p>
                  )}
                  {data.technologies?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-black uppercase text-slate-500 mb-2">Featured Technologies</p>
                      <div className="flex flex-wrap gap-1.5">
                        {data.technologies.map((t, k) => (
                          <span key={k} className="text-xs px-2 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-800 font-bold">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {tab === "downloads" && (
                <div>
                  {data.downloads?.length > 0 ? (
                    <ul className="space-y-2">
                      {data.downloads.map((d, i) => {
                        const isLocal = d.mirrored === true || (d.url || "").startsWith("/api/");
                        const href = isLocal ? `${API}${d.url}` : d.url;
                        const thumbHref = d.thumbnail_url ? `${API}${d.thumbnail_url}` : null;
                        return (
                          <li key={i}>
                            <a
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`ansell-specs-download-${i}`}
                              className={`flex items-center gap-3 p-3 rounded-lg border group ${
                                isLocal
                                  ? "border-emerald-200 bg-emerald-50/40 hover:border-emerald-500"
                                  : "border-slate-200 hover:border-blue-500 hover:bg-blue-50"
                              }`}
                            >
                              {thumbHref ? (
                                <img
                                  src={thumbHref}
                                  alt=""
                                  className="w-12 h-16 object-cover rounded border border-slate-200 shadow-sm shrink-0"
                                />
                              ) : (
                                <div className={`w-12 h-16 rounded flex items-center justify-center text-[10px] font-black shrink-0 ${
                                  isLocal ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                                }`}>PDF</div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-slate-800 group-hover:text-blue-700 truncate">
                                  {d.label}
                                </p>
                                <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                  {isLocal ? (
                                    <>
                                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                      Hosted on sterilemate.com {d.size_kb ? `· ${d.size_kb} KB` : ""}
                                    </>
                                  ) : (
                                    <>
                                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400" />
                                      External · ansell.com
                                    </>
                                  )}
                                </p>
                              </div>
                              <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15V3m0 12l-4-4m4 4l4-4M4 21h16" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500">No datasheets available yet.</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
